import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useAvailableAnimals, useShelters, useAdopterProfile, useAuth,
  generateMatchReason, isGeminiConfigured, getImageUrl,
  saveMatchSession, loadMatchSession, clearMatchSession, saveLifestyleToProfile,
  LIFESTYLE_QUESTIONS, EMPTY_LIFESTYLE_PROFILE, countAnsweredLifestyle, isLifestyleComplete,
  lifestyleFromAdopterProfile,
  haversineKm, isUsableCoordinate, distanceMatchScore, formatDistanceKm, DEFAULT_MAX_TRAVEL_KM,
} from '@rehome/shared';
import type { Animal, Shelter, LifestyleProfile, MatchResultEntry } from '@rehome/shared';
import {
  Heart, Home, Activity, Sparkles, Dog, Cat, ArrowRight, RefreshCw,
  BookmarkCheck, Info, MapPin, UserCheck, Loader2, Pencil,
} from 'lucide-react';

// ─── Deterministic lifestyle scoring (plan.md 4.2.3: the FILTERING is SQL /
// deterministic — Gemini only writes the creative reason text afterwards) ───

const scoreAnimal = (a: Animal, p: LifestyleProfile): number => {
  let score = 50;
  const traits = (a.traits || []).map(t => t.toLowerCase());
  const has = (...names: string[]) => names.some(n => traits.some(t => t.includes(n)));

  // Housing
  if (p.housing === 'apartment_small') {
    if (a.species === 'Macska') score += 20;
    else if (a.weightKg != null && a.weightKg <= 12) score += 10;
    else if (a.weightKg != null && a.weightKg > 25) score -= 20;
    if (has('nyugodt', 'csendes')) score += 8;
  } else if (p.housing === 'apartment_large') {
    if (a.weightKg != null && a.weightKg > 35) score -= 8;
  } else if (p.housing === 'house_garden') {
    if (a.species === 'Kutya') score += 10;
    if (has('energikus', 'aktív')) score += 6;
  }

  // Activity
  if (p.activity === 'very_active') {
    if (has('energikus', 'aktív', 'játékos') || a.activityLevel === 'high' || a.activityLevel === 'very_high') score += 18;
    if (has('nyugodt')) score -= 6;
  } else if (p.activity === 'moderate') {
    if (has('barátságos', 'játékos')) score += 10;
  } else if (p.activity === 'low') {
    if (has('nyugodt', 'lusta', 'bújós') || a.activityLevel === 'low') score += 18;
    if (has('energikus') || a.activityLevel === 'very_high') score -= 15;
  }

  // Experience
  if (p.experience === 'first_time') {
    if (has('barátságos', 'kezes', 'nyugodt')) score += 12;
    if (has('félénk', 'domináns', 'makacs')) score -= 12;
  } else {
    score += 4; // experienced owners handle anything a bit better
  }

  // Other pets
  if (p.otherPets === 'dog' || p.otherPets === 'cat') {
    if (has('barátságos', 'szociális', 'játékos')) score += 8;
    if (has('domináns', 'féltékeny')) score -= 10;
  }

  // Kids
  if (p.kids === 'young') {
    if (has('gyerekbarát', 'barátságos', 'türelmes')) score += 15;
    if (has('félénk', 'ideges')) score -= 15;
  } else if (p.kids === 'older') {
    if (has('játékos', 'barátságos')) score += 8;
  }

  return Math.max(5, Math.min(99, Math.round(score)));
};

const profileToQuery = (p: LifestyleProfile): string => {
  const housing: Record<string, string> = { house_garden: 'kertes házban él', apartment_large: 'nagy lakásban él', apartment_small: 'kis lakásban él' };
  const activity: Record<string, string> = { very_active: 'nagyon aktív életmódot folytat', moderate: 'szeret sétálni', low: 'nyugodt életmódot folytat' };
  const experience: Record<string, string> = { experienced: 'tapasztalt gazdi', first_time: 'először lesz állata' };
  const pets: Record<string, string> = { none: 'nincs más állata', dog: 'van kutyája', cat: 'van macskája' };
  const kids: Record<string, string> = { none: 'nincsenek gyerekek a háztartásban', young: 'kisgyerek van a családban', older: 'nagyobb gyerekek vannak a családban' };
  return [housing[p.housing], activity[p.activity], experience[p.experience], pets[p.otherPets], kids[p.kids]].filter(Boolean).join(', ');
};

/** Ikon a kérdésekhez — csak dekoráció, ezért nem került a közös definícióba. */
const QUESTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  housing: Home, activity: Activity, experience: Sparkles, otherPets: Dog, kids: Sparkles,
};
const OPTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  cat: Cat, dog: Dog,
};

/**
 * Melyik képernyőn állunk:
 *  - `loading`      — a profil még töltődik, ne villanjon fel rossz nézet
 *  - `confirmSaved` — minden válasz megvan a profilban: futtassuk azzal, vagy nézzük át?
 *  - `quiz`         — a szokásos végigkattintós folyamat (ismert válaszok előre bejelölve)
 */
type Screen = 'loading' | 'confirmSaved' | 'quiz';

const GamifiedMatcherPage: React.FC = () => {
  const { data: animalsData } = useAvailableAnimals();
  const { data: sheltersData } = useShelters();
  const { data: adopterProfile, loading: profileLoading } = useAdopterProfile();
  const { user } = useAuth();
  const animals = animalsData ?? [];

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState<MatchResultEntry[] | null>(null);
  const [profile, setProfile] = useState<LifestyleProfile>(EMPTY_LIFESTYLE_PROFILE);
  const [screen, setScreen] = useState<Screen>('loading');
  const [restored, setRestored] = useState(false);
  const [restoredAt, setRestoredAt] = useState<string | null>(null);
  /** A profilból hozott (tehát nem most megadott) válaszok száma — a UI ezt jelzi. */
  const [prefilledCount, setPrefilledCount] = useState(0);

  // ─── Menhely-koordináták ID szerint ──────────────────────────────────
  const shelterById = useMemo(() => {
    const map = new Map<string, Shelter>();
    (sheltersData ?? []).forEach(s => map.set(s.id, s));
    return map;
  }, [sheltersData]);

  const homeCoords = isUsableCoordinate(adopterProfile?.homeLat, adopterProfile?.homeLng)
    ? { lat: adopterProfile!.homeLat as number, lng: adopterProfile!.homeLng as number }
    : null;
  const maxTravelKm = adopterProfile?.maxTravelDistanceKm ?? DEFAULT_MAX_TRAVEL_KM;

  /** Az állat menhelyének távolsága a felhasználó lakóhelyétől (km), vagy null. */
  const distanceToAnimal = useMemo(() => (animal: Animal): number | null => {
    if (!homeCoords) return null;
    const shelter = shelterById.get(animal.shelterId);
    if (!shelter || !isUsableCoordinate(shelter.lat, shelter.lng)) return null;
    return haversineKm(homeCoords, { lat: shelter.lat as number, lng: shelter.lng as number });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeCoords?.lat, homeCoords?.lng, shelterById]);

  // ─── Belépő állapot eldöntése ────────────────────────────────────────
  // Sorrend számít: egy már lefuttatott matching (matchSession) mindig
  // erősebb, mint a profilból induló új futás — a felhasználó épp azt
  // nézegette, mielőtt elnavigált.
  useEffect(() => {
    if (profileLoading) return;

    const saved = loadMatchSession();
    if (saved) {
      setProfile(saved.profile);
      setMatches(saved.results);
      setRestored(true);
      setRestoredAt(saved.savedAt);
      setScreen('quiz');
      return;
    }

    const fromProfile = lifestyleFromAdopterProfile(adopterProfile);
    const answered = countAnsweredLifestyle(fromProfile);

    if (answered > 0) {
      // A már ismert válaszok előre bejelölve — a felhasználó bármelyiket
      // felülírhatja, hiszen épp az a kérdés, változott-e valami.
      setProfile(fromProfile);
      setPrefilledCount(answered);
    }

    setScreen(isLifestyleComplete(fromProfile) ? 'confirmSaved' : 'quiz');
  }, [profileLoading, adopterProfile]);

  const questions = LIFESTYLE_QUESTIONS;

  const handleSelect = (key: string, value: string) => {
    // Build the updated profile synchronously — calling runMatch() right after
    // setProfile() would read the STALE profile (the last answer would be lost).
    const nextProfile = { ...profile, [key]: value } as LifestyleProfile;
    setProfile(nextProfile);
    if (step < questions.length - 1) { setStep(prev => prev + 1); } else { runMatch(nextProfile); }
  };

  const runMatch = async (p: LifestyleProfile) => {
    setLoading(true);

    // 1. Deterministic scoring over the SQL-fetched Available animals —
    //    the selection itself never depends on the LLM (plan.md).
    //    A menhely távolsága MÓDOSÍTJA a pontszámot, de nem szűr ki senkit:
    //    a távolabbi állat is megjelenik, csak hátrébb (l. shared/src/geo.ts).
    const scored = animals
      .map(a => {
        const km = distanceToAnimal(a);
        return {
          animal: a,
          distanceKm: km,
          score: Math.max(5, Math.min(99, scoreAnimal(a, p) + distanceMatchScore(km, maxTravelKm))),
        };
      })
      .sort((x, y) => y.score - x.score)
      .slice(0, 3);

    // 2. Optional Gemini garnish: creative Hungarian reason per match.
    //    Graceful degradation: without an API key we fall back to a
    //    trait-based template.
    const query = profileToQuery(p);
    const reasons = await Promise.all(
      scored.map(({ animal }) =>
        isGeminiConfigured()
          ? generateMatchReason(
              { name: animal.name, species: animal.species, breed: animal.breed, traits: animal.traits || [] },
              query
            ).catch(() => null)
          : Promise.resolve(null)
      )
    );

    const results: MatchResultEntry[] = scored.map(({ animal, score }, idx) => ({
      animalId: animal.id,
      score,
      reason:
        reasons[idx] ||
        `${animal.name} ${(animal.traits || []).slice(0, 2).join(' és ').toLowerCase() || 'kedves'} természete jól illik az életmódodhoz.`,
    }));

    setMatches(results);
    // Azonnal elmentjük — a felhasználó a következő kattintásával már el is
    // navigálhat (pl. a login falba), és onnan ide kell tudnia visszatalálni.
    saveMatchSession(p, results);
    // Bejelentkezett felhasználónál a válaszok a profilba is bekerülnek, így
    // a következő matchinget nem kell újra végigkattintani.
    void saveLifestyleToProfile(p);
    setRestored(false);
    setRestoredAt(null);
    setLoading(false);
  };

  /** Teljes újrakezdés: a mentett futás is törlődik, nem éledhet fel újra. */
  const handleRestart = () => {
    clearMatchSession();
    setMatches(null);
    setStep(0);
    setRestored(false);
    setRestoredAt(null);
    // A profilból ismert válaszokat MEGTARTJUK előre bejelölve: az
    // "újrakezdés" a találatokra vonatkozik, nem arra, hogy a rendszer
    // elfelejtse, amit már tud a felhasználóról.
    const fromProfile = lifestyleFromAdopterProfile(adopterProfile);
    setProfile(countAnsweredLifestyle(fromProfile) > 0 ? fromProfile : EMPTY_LIFESTYLE_PROFILE);
    setScreen('quiz');
  };

  if (screen === 'loading' || (profileLoading && !matches)) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <div className="relative mb-8"><Heart className="w-20 h-20 text-rose-500 animate-pulse" /><Sparkles className="w-8 h-8 text-yellow-400 absolute -top-2 -right-2" /></div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Keressük a társadat...</h2>
        <p className="text-gray-500">Összevetjük az életstílusodat a lakóink személyiségével.</p>
      </div>
    );
  }

  // ═══ A profilban MINDEN válasz megvan — futtassuk azzal, vagy nézzük át? ═══
  if (screen === 'confirmSaved' && !matches) {
    return (
      <div className="max-w-2xl mx-auto py-10 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-teal-100 text-teal-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <UserCheck className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Megvannak a válaszaid</h1>
          <p className="text-gray-600">
            A profilodban már szerepel minden, amit a Smart Match kérdezne. Futtassuk ezekkel,
            vagy változott azóta valami?
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
          {questions.map(q => {
            const selected = q.options.find(o => o.value === profile[q.key]);
            const Icon = QUESTION_ICONS[q.key] ?? Sparkles;
            return (
              <div key={q.key} className="flex items-center gap-3 px-5 py-3">
                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-500 flex-1">{q.profileLabel}</span>
                <span className="text-sm font-semibold text-gray-800">{selected?.label ?? '—'}</span>
              </div>
            );
          })}
          {adopterProfile?.homeCity && (
            <div className="flex items-center gap-3 px-5 py-3">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-500 flex-1">Lakóhely · vállalt távolság</span>
              <span className="text-sm font-semibold text-gray-800">
                {adopterProfile.homeCity} · {maxTravelKm} km
              </span>
            </div>
          )}
        </div>

        {!homeCoords && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>
              Nincs megadva a lakóhelyed, ezért a menhely távolsága most nem számít bele a
              pontszámba.{' '}
              <Link to="/profile" className="font-semibold underline">Add meg a profilodban</Link>, és
              a közelebbi menhelyek állatai előrébb kerülnek.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => runMatch(profile)}
            className="bg-teal-600 text-white py-3.5 rounded-xl font-bold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" /> Futtatás ezekkel
          </button>
          <button
            onClick={() => { setScreen('quiz'); setStep(0); }}
            className="bg-white border-2 border-gray-200 text-gray-700 py-3.5 rounded-xl font-bold hover:border-teal-300 hover:text-teal-700 transition-colors flex items-center justify-center gap-2"
          >
            <Pencil className="w-5 h-5" /> Változott valami
          </button>
        </div>
      </div>
    );
  }

  if (matches) {
    // Empty results from Gemini
    if (matches.length === 0) {
      return (
        <div className="max-w-2xl mx-auto text-center py-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Nincs találat</h2>
          <p className="text-gray-600 mb-6">Jelenleg nincs elérhető állat, amit párosíthatnánk. Próbáld újra később, vagy böngéssz a teljes listában!</p>
          <div className="flex gap-4 justify-center">
            <button onClick={handleRestart} className="bg-teal-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-teal-700">
              <RefreshCw className="w-4 h-4 inline mr-1" /> Újrakezdés
            </button>
            <Link to="/animals" className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-200">
              Összes állat
            </Link>
          </div>
        </div>
      );
    }

    // A visszatöltött találatok között lehet olyan állat, akit időközben
    // örökbe fogadtak — ilyenkor már nincs benne az `Available` listában.
    // Nem hazudunk róla: kihagyjuk a kártyák közül, és külön jelezzük.
    const stillAvailable = matches.filter(m => animals.some(a => a.id === m.animalId));
    const goneCount = matches.length - stillAvailable.length;

    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center"><h1 className="text-3xl font-bold text-gray-900 mb-2">Megtaláltuk az Igazit! ❤️</h1><p className="text-gray-600">Az életstílusod alapján ezek az állatok illenek hozzád a legjobban.</p></div>

        {/* Visszatöltött futás — a user nem most kattintotta végig a kérdéseket */}
        {restored && (
          <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-xl p-4 text-sm text-teal-900">
            <BookmarkCheck className="w-5 h-5 flex-shrink-0 text-teal-600 mt-0.5" />
            <div>
              <p className="font-semibold">Megőriztük a korábbi találataidat.</p>
              <p className="text-teal-800/80">
                {restoredAt && `A matchinget ${new Date(restoredAt).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}-n futtattad. `}
                Nem kell újra végigkattintanod a kérdéseket — ha viszont változott az életmódod, indítsd újra alul.
              </p>
            </div>
          </div>
        )}

        {/* Be nem jelentkezett user: a találatok megmaradnak, a fiók ráér */}
        {!user && (
          <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
            <Info className="w-5 h-5 flex-shrink-0 text-gray-400 mt-0.5" />
            <p>
              Nyugodtan nézelődj — <span className="font-medium text-gray-800">a találataidat elmentettük ebben a böngészőben</span>, és
              akkor is itt lesznek, ha közben belépsz vagy regisztrálsz. Fiók csak az örökbefogadási igény leadásához kell.
            </p>
          </div>
        )}

        {goneCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            {goneCount === matches.length
              ? 'A korábbi találataid időközben mind gazdira találtak. Indítsd újra a matchinget a friss listával!'
              : `${goneCount} korábbi találatod időközben gazdira talált, ezért kikerült a listából.`}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stillAvailable.map((match, idx) => {
            const animal = animals.find(a => a.id === match.animalId);
            if (!animal) return null;
            const shelter = shelterById.get(animal.shelterId);
            const distance = formatDistanceKm(distanceToAnimal(animal));
            return (
              <div key={animal.id} className={`bg-white rounded-2xl overflow-hidden shadow-lg border-2 ${idx === 0 ? 'border-rose-400 scale-105 z-10' : 'border-gray-100'} hover:shadow-xl transition-all relative group`}>
                {idx === 0 && <div className="absolute top-0 left-0 w-full bg-rose-500 text-white text-center text-xs font-bold py-1 z-20">TÖKÉLETES PÁR</div>}
                <div className="h-56 relative bg-gray-200">
                  {getImageUrl(animal) ? (
                    <img src={getImageUrl(animal)!} className="w-full h-full object-cover" alt={animal.name} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">Nincs kép</div>
                  )}
                  <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h3 className="text-white font-bold text-xl">{animal.name}</h3><p className="text-white/80 text-sm">{animal.breed}</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2"><div className="bg-rose-500 h-2 rounded-full" style={{ width: `${match.score}%` }} /></div>
                    <span className="font-bold text-rose-600">{match.score}%</span>
                  </div>
                  {/* Távolság: csak akkor, ha tudjuk — sose tippelünk. */}
                  {(distance || shelter) && (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-4">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">
                        {shelter?.name}
                        {distance && <span className="font-semibold text-gray-700"> · {distance}</span>}
                      </span>
                    </p>
                  )}
                  <p className="text-sm text-gray-600 italic mb-6">"{match.reason || 'Összeillő páros!'}"</p>
                  {/* A kártya a NYILVÁNOS állatprofilra visz, nem egyből az
                      örökbefogadási űrlapra. Korábban ez utóbbira mutatott,
                      így a be nem jelentkezett user a találatra kattintva
                      azonnal login falba futott — pont mielőtt bármit
                      megtudott volna az állatról. A fiókot ott kérjük, ahol
                      valódi tétje van: az igény leadásánál. */}
                  <Link to={`/animals/${animal.id}`} className="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2">
                    Megismerem <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {!homeCoords && (
          <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
            <MapPin className="w-5 h-5 flex-shrink-0 text-gray-400 mt-0.5" />
            <p>
              A távolság most nem számított bele a pontszámba.{' '}
              <Link to="/profile" className="text-teal-600 font-semibold hover:underline">Add meg a lakóhelyed a profilodban</Link>, és
              a közelebbi menhelyek állatai előrébb kerülnek.
            </p>
          </div>
        )}

        <div className="text-center pt-8 space-y-3">
          <Link to="/animals" className="block text-teal-600 font-semibold hover:underline">Vagy nézd meg az összes gazdikeresőt →</Link>
          <button onClick={handleRestart} className="text-gray-500 hover:text-teal-600 flex items-center gap-2 mx-auto"><RefreshCw className="w-4 h-4" /> Smart Match újraindítása</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[step];
  const handleBack = () => { if (step > 0) setStep(prev => prev - 1); };
  const QIcon = QUESTION_ICONS[currentQ.key] ?? Sparkles;

  return (
    <div className="max-w-2xl mx-auto h-[70vh] flex flex-col justify-center">
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-500 mb-2"><span>Lépés {step + 1} / {questions.length}</span><span>Párosítás...</span></div>
        <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-teal-500 h-2 rounded-full transition-all duration-500" style={{ width: `${((step + 1) / questions.length) * 100}%` }} /></div>
      </div>

      {/* A profilból hozott válaszok jelzése — hogy a felhasználó értse, miért
          van már bejelölve valami, és hogy nyugodtan felülírhatja. */}
      {prefilledCount > 0 && !isLifestyleComplete(profile) && step === 0 && (
        <div className="mb-6 flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-xl p-4 text-sm text-teal-900">
          <UserCheck className="w-5 h-5 flex-shrink-0 text-teal-600 mt-0.5" />
          <p>
            <span className="font-semibold">{prefilledCount} választ már ismerünk a profilodból</span> — előre
            bejelöltük őket. Ha változott valami, egyszerűen válassz mást.
          </p>
        </div>
      )}

      <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center flex items-center justify-center gap-3">
        <QIcon className="w-7 h-7 text-teal-600" />
        {currentQ.question}
      </h2>
      <div className="grid grid-cols-1 gap-4">
        {currentQ.options.map(opt => {
          const Icon = OPTION_ICONS[opt.value] ?? QUESTION_ICONS[currentQ.key] ?? Sparkles;
          const isSelected = profile[currentQ.key] === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => handleSelect(currentQ.key, opt.value)}
              className={`flex items-center p-6 rounded-2xl border-2 transition-all group shadow-sm ${
                isSelected
                  ? 'border-teal-500 bg-teal-50'
                  : 'bg-white border-gray-100 hover:border-teal-400 hover:bg-teal-50'
              }`}
            >
              <div className={`p-3 rounded-full mr-4 transition-colors ${isSelected ? 'bg-white' : 'bg-gray-100 group-hover:bg-white'}`}>
                <Icon className={`w-6 h-6 ${isSelected ? 'text-teal-600' : 'text-gray-600 group-hover:text-teal-600'}`} />
              </div>
              <span className={`text-lg font-medium ${isSelected ? 'text-teal-800' : 'text-gray-700 group-hover:text-teal-800'}`}>{opt.label}</span>
              {isSelected && <span className="ml-auto text-xs font-bold text-teal-600 uppercase tracking-wide">Jelenlegi</span>}
              {!isSelected && <ArrowRight className="w-5 h-5 ml-auto text-gray-300 group-hover:text-teal-500" />}
            </button>
          );
        })}
      </div>
      {step > 0 && (
        <button onClick={handleBack} className="mt-4 text-gray-500 hover:text-teal-600 text-sm font-medium self-start">
          ← Vissza
        </button>
      )}
    </div>
  );
};

export default GamifiedMatcherPage;
